import { useState, useMemo, useEffect } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  Star, TrendingUp, MessageSquareQuote,
  ThumbsUp, Filter, Search, ChevronDown, EyeOff, Pencil, BarChart2, Flag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { useMyTeacherReviews, useUpsertReply, useFlagReview } from "@/hooks/use-reviews";
import { toast } from "@/hooks/use-toast";
import { initialsFrom } from "@/lib/roleDisplay";
import type { TeacherOwnReviewResponse } from "@/services/reviewService";

type SortOption = "newest" | "oldest" | "highest" | "lowest";
type FilterRating = "all" | "5" | "4" | "3";

const PAGE_SIZE = 20;
const EMPTY_REVIEWS: TeacherOwnReviewResponse[] = [];

function ReviewReplyComposer({ review }: { review: TeacherOwnReviewResponse }) {
  const upsertReply = useUpsertReply();
  const hasReply = review.teacherReply != null;
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(review.teacherReply ?? "");

  // Keep the draft in sync with the server value whenever we're not actively editing
  // (e.g. after a refetch following a successful save).
  useEffect(() => {
    if (!isEditing) setDraft(review.teacherReply ?? "");
  }, [review.teacherReply, isEditing]);

  const handleSubmit = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    upsertReply.mutate(
      { reviewId: review.id, reply: trimmed },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  if (hasReply && !isEditing) {
    return (
      <div className="mt-3 pl-4 border-l-2 border-indigo-200 bg-indigo-50/60 rounded-r-lg py-2.5 px-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-xs font-semibold text-indigo-700">Your reply</p>
          <button
            type="button"
            onClick={() => {
              setDraft(review.teacherReply ?? "");
              setIsEditing(true);
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <Pencil className="w-3 h-3" />
            Edit reply
          </button>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{review.teacherReply}</p>
      </div>
    );
  }

  return (
    <div className="mt-3 flex gap-2 items-end">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Write a reply to this review..."
        rows={2}
        maxLength={1000}
        className="flex-1 resize-none text-sm min-h-[44px] bg-muted/40"
        aria-label="Reply to review"
      />
      <div className="flex flex-col gap-1.5 shrink-0">
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!draft.trim() || upsertReply.isPending}
          className="h-9 cursor-pointer"
        >
          {upsertReply.isPending ? "Saving…" : hasReply ? "Save" : "Reply"}
        </Button>
        {hasReply && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsEditing(false);
              setDraft(review.teacherReply ?? "");
            }}
            disabled={upsertReply.isPending}
            className="h-7 text-xs cursor-pointer"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

export default function TeacherReviewsPage() {
  usePageTitle('My Reviews | BookMyCoaching');
  const { teacherProfile } = useTeacherProfile();
  const teacherName = teacherProfile?.fullName || "";
  const teacherAvatar = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || []).map((s: any) =>
    typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || ""
  ).filter(Boolean);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [filterRating, setFilterRating] = useState<FilterRating>("all");
  const [page, setPage] = useState(0);

  const { data, isLoading, isError } = useMyTeacherReviews(page, PAGE_SIZE);
  const flagReview = useFlagReview();

  const handleFlag = (reviewId: string) => {
    flagReview.mutate(
      { reviewId },
      {
        onSuccess: () =>
          toast({ title: "Review flagged", description: "Our moderators will take a look." }),
        onError: () => toast({ title: "Couldn't flag the review. Please try again.", variant: "destructive" }),
      }
    );
  };

  const reviews: TeacherOwnReviewResponse[] = data?.content ?? EMPTY_REVIEWS;
  const totalPages = data?.totalPages ?? 0;

  // Compute stats (derived from the currently loaded page — the API has no
  // separate cross-page aggregate endpoint for a teacher's own reviews).
  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews : 0;
  const ratingDistribution = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    percentage: totalReviews > 0 ? (reviews.filter((r) => r.rating === star).length / totalReviews) * 100 : 0,
  }));
  const fiveStarPercentage = totalReviews > 0
    ? Math.round((reviews.filter((r) => r.rating === 5).length / totalReviews) * 100)
    : 0;

  // Filter & sort
  const filteredReviews = useMemo(() => {
    let result = [...reviews];

    if (filterRating !== "all") {
      result = result.filter((r) => r.rating === parseInt(filterRating));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.comment.toLowerCase().includes(q) ||
          r.authorDisplayName.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "newest":
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "oldest":
        result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case "highest":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "lowest":
        result.sort((a, b) => a.rating - b.rating);
        break;
    }

    return result;
  }, [reviews, filterRating, searchQuery, sortBy]);

  const sortLabels: Record<SortOption, string> = {
    newest: "Newest First",
    oldest: "Oldest First",
    highest: "Highest Rated",
    lowest: "Lowest Rated",
  };

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />

          <div className="flex-1 flex flex-col min-w-0">
            {/* Page header — outside padded main so border-b runs edge-to-edge */}
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
              <h1 className="text-2xl font-bold text-slate-900">Reviews</h1>
              <p className="text-sm text-slate-500">See what your students are saying about your sessions</p>
            </div>
            {/* Main Content */}
            <main className="flex-1 p-4 md:p-6 overflow-auto bg-slate-50">

              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Overall Rating */}
                <div className="bg-white rounded-2xl shadow-md p-5 group transition-shadow hover:shadow-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Star className="w-4.5 h-4.5 fill-amber-400 text-amber-400" />
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    {totalReviews > 0 ? (
                      <>
                        <span className="font-display text-4xl font-bold text-foreground">
                          {averageRating.toFixed(1)}
                        </span>
                        <span className="text-sm text-slate-500">/5</span>
                      </>
                    ) : (
                      <span className="font-display text-4xl font-bold text-foreground">—</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {totalReviews > 0 ? "Average Rating" : "No ratings yet"}
                  </p>
                </div>

                {/* Total Reviews */}
                <div className="bg-white rounded-2xl shadow-md p-5 group transition-shadow hover:shadow-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <MessageSquareQuote className="w-4.5 h-4.5 text-indigo-500" />
                    </div>
                  </div>
                  <span className="font-display text-4xl font-bold text-foreground">
                    {totalReviews}
                  </span>
                  <p className="text-sm text-slate-500 mt-1">Total Reviews</p>
                </div>

                {/* 5-Star Rate */}
                <div className="bg-white rounded-2xl shadow-md p-5 group transition-shadow hover:shadow-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <ThumbsUp className="w-4.5 h-4.5 text-emerald-500" />
                    </div>
                  </div>
                  <span className="font-display text-4xl font-bold text-foreground">
                    {fiveStarPercentage}%
                  </span>
                  <p className="text-sm text-slate-500 mt-1">5-Star Reviews</p>
                </div>

                {/* Trend */}
                <div className="bg-white rounded-2xl shadow-md p-5 group transition-shadow hover:shadow-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center">
                      <TrendingUp className="w-4.5 h-4.5 text-violet-500" />
                    </div>
                  </div>
                  <span className="font-display text-4xl font-bold text-foreground">
                    +12%
                  </span>
                  <p className="text-sm text-slate-500 mt-1">vs Last Month</p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Left — Rating Breakdown */}
                <div className="xl:col-span-1 space-y-6">
                  {/* Rating Distribution */}
                  <div className="bg-white rounded-2xl shadow-md p-5">
                    <h3 className="inline-flex items-center gap-2 font-bold text-base text-slate-800 mb-4">
                      <BarChart2 className="w-4 h-4 text-slate-400" />
                      Rating Breakdown
                    </h3>
                    <div className="space-y-3">
                      {ratingDistribution.map(({ star, count, percentage }) => (
                        <button
                          key={star}
                          onClick={() =>
                            setFilterRating(
                              filterRating === String(star) ? "all" : (String(star) as FilterRating)
                            )
                          }
                          className={cn(
                            "w-full flex items-center gap-3 group/bar rounded-lg px-2 py-1.5 -mx-2 transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            filterRating === String(star)
                              ? "bg-primary/5"
                              : "hover:bg-muted/50"
                          )}
                        >
                          <div className="flex items-center gap-1 w-12 shrink-0">
                            <span className="text-sm font-semibold text-slate-700">{star}</span>
                            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          </div>
                          <div className="flex-1">
                            <Progress
                              value={percentage}
                              className="h-3 bg-muted/60"
                            />
                          </div>
                          <span className="text-sm font-semibold text-slate-600 w-8 text-right tabular-nums">
                            {count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right — Reviews List */}
                <div className="xl:col-span-2">
                  {/* Search & Sort Bar */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search reviews..."
                        className="pl-9 h-10 bg-white shadow-sm border-0"
                      />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2 h-10 shrink-0 cursor-pointer">
                          <Filter className="w-3.5 h-3.5" />
                          {sortLabels[sortBy]}
                          <ChevronDown className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(Object.keys(sortLabels) as SortOption[]).map((key) => (
                          <DropdownMenuItem
                            key={key}
                            onClick={() => setSortBy(key)}
                            className={cn(
                              "cursor-pointer",
                              sortBy === key && "font-semibold text-primary"
                            )}
                          >
                            {sortLabels[key]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Results Count */}
                  <p className="text-xs text-muted-foreground mb-4">
                    Showing {filteredReviews.length} of {totalReviews} reviews
                    {filterRating !== "all" && (
                      <span>
                        {" "}· {filterRating}
                        <Star className="w-3 h-3 text-warning fill-warning inline ml-0.5 -mt-0.5" />
                      </span>
                    )}
                  </p>

                  {isError && (
                    <div className="bg-red-50 rounded-2xl shadow-sm p-4 text-sm text-red-700 mb-4">
                      Couldn't load your reviews. Please try again later.
                    </div>
                  )}

                  {/* Review Cards */}
                  <div className="space-y-4">
                    {isLoading ? (
                      <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                        <p className="text-sm text-muted-foreground">Loading reviews…</p>
                      </div>
                    ) : filteredReviews.length === 0 ? (
                      <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                        <MessageSquareQuote className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                        <p className="font-medium text-foreground mb-1">
                          No reviews found
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Try adjusting your filters or search query
                        </p>
                      </div>
                    ) : (
                      filteredReviews.map((review) => (
                        <div
                          key={review.id}
                          className="bg-white rounded-2xl shadow-md p-5 hover:shadow-lg transition-all duration-200 group"
                        >
                          <div className="flex items-start gap-4">
                            <Avatar className="w-11 h-11 rounded-xl ring-2 ring-slate-100 shrink-0">
                              <AvatarFallback className="rounded-xl bg-indigo-50 text-indigo-600 text-sm font-semibold">
                                {initialsFrom(review.authorDisplayName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div>
                                  <p className="font-semibold text-sm text-slate-900">
                                    {review.authorDisplayName}
                                  </p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs text-slate-400">
                                      {new Date(review.createdAt).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </span>
                                    {review.hidden && (
                                      <Badge
                                        variant="warning"
                                        className="text-xs px-2 py-0 h-5 font-medium gap-1"
                                      >
                                        <EyeOff className="w-2.5 h-2.5" />
                                        Hidden by admin
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <Star
                                      key={i}
                                      className={cn(
                                        "w-3.5 h-3.5",
                                        i < review.rating
                                          ? "text-amber-400 fill-amber-400"
                                          : "text-slate-200"
                                      )}
                                    />
                                  ))}
                                </div>
                              </div>
                              <p className="text-sm text-slate-600 leading-relaxed">
                                {review.comment}
                              </p>

                              {/* Reply moderation state — driven by replyStatus, not `hidden` */}
                              {(review.replyStatus === "PENDING" || review.replyStatus === "HELD") && (
                                <Badge
                                  variant="warning"
                                  className="mt-2 text-xs px-2 py-0 h-5 font-medium gap-1"
                                >
                                  Reply under review
                                </Badge>
                              )}
                              {review.replyStatus === "REMOVED" && (
                                <Badge
                                  variant="destructive"
                                  className="mt-2 text-xs px-2 py-0 h-5 font-medium gap-1"
                                >
                                  Reply removed
                                </Badge>
                              )}

                              <ReviewReplyComposer review={review} />

                              {/* Flag for review — non-hiding; queues the review for a moderator */}
                              <div className="mt-3 flex justify-end">
                                {review.flaggedAt != null ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                                    <Flag className="w-3 h-3 fill-amber-500 text-amber-500" />
                                    Flagged for review
                                  </span>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleFlag(review.id)}
                                    disabled={flagReview.isPending}
                                    className="h-7 px-2 text-xs text-slate-400 hover:text-amber-600 cursor-pointer gap-1"
                                  >
                                    <Flag className="w-3 h-3" />
                                    Flag for review
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0 || isLoading}
                        className="cursor-pointer"
                      >
                        Previous
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Page {page + 1} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={page + 1 >= totalPages || isLoading}
                        className="cursor-pointer"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}
